import { Box, Divider } from '@mui/material'
import { useTranslation } from 'react-i18next'

import { GapBox } from 'components/Box'
import { BoxForm } from 'components/Form'
import { SectionHeader } from 'components/Header'
import { TextLink } from 'components/Link'
import Loading from 'components/Loading'
import Paper from 'components/Paper'
import Tooltip from 'components/Tooltip'
import { SetupPrebidStackSectionContext } from 'features/globalSetup/contexts/SetupPrebidStackSectionContext'
import { BannerAndOutstreamForm } from 'features/globalSetup/forms/GoogleAdManager/BannerAndOutstreamForm'
import { InstreamForm } from 'features/globalSetup/forms/GoogleAdManager/InstreamForm'
import { NativeForm } from 'features/globalSetup/forms/GoogleAdManager/NativeForm'
import { GamSetupOrderTypeDTO } from 'features/workspace/api/types/gamSetupOrderDTO'
import { KNOWLEDGE_BASE_URL } from './constants'
import { useGetFreshStatusAndErrorForGamSetupOrders } from './hooks/useGetFreshStatusAndErrorForGamSetupOrders'
import { GamSetupOrderSlice, useGetGamSetupOrders } from './hooks/useGetGamSetupOrders'
import { useGetSectionAvailability } from './hooks/useGetSectionAvailability'

export const SetupPrebidStackSection = (): React.JSX.Element => {
  const { t: tc } = useTranslation('common')
  const { t } = useTranslation('features/globalSetup', {
    keyPrefix: 'googleAdManagerConnection.setupPrebidStack',
  })
  const { available, styles, tooltip } = useGetSectionAvailability()

  const { gamSetupOrders, loading: loadingGamSetupOrders } = useGetGamSetupOrders()

  const { getFreshStatusAndError } = useGetFreshStatusAndErrorForGamSetupOrders()

  if (loadingGamSetupOrders) {
    return <Loading />
  }

  const getSetup = (setupType: GamSetupOrderTypeDTO): GamSetupOrderSlice | undefined =>
    gamSetupOrders.find((order: GamSetupOrderSlice) => order.setupType === setupType)

  return (
    <SetupPrebidStackSectionContext.Provider value={{ available }}>
      <Paper data-cy='setup-prebid-stack-section'>
        <Tooltip
          content={tooltip}
          placement='auto'
        >
          <Box sx={styles}>
            <SectionHeader
              subtitle={t('header.description')}
              subtitleEndAdornment={
                <TextLink
                  link={{
                    title: tc('learnMore'),
                    url: KNOWLEDGE_BASE_URL,
                  }}
                />
              }
              title={t('header.title')}
            />
            <GapBox />

            <BoxForm>
              <BannerAndOutstreamForm
                gamSetupOrder={getSetup('bannerOutstream')}
                getFreshStatusAndError={getFreshStatusAndError}
              />
              <Divider />

              <InstreamForm
                gamSetupOrder={getSetup('instream')}
                getFreshStatusAndError={getFreshStatusAndError}
              />
              <Divider />

              <NativeForm
                gamSetupOrder={getSetup('native')}
                getFreshStatusAndError={getFreshStatusAndError}
              />
            </BoxForm>
          </Box>
        </Tooltip>
      </Paper>
    </SetupPrebidStackSectionContext.Provider>
  )
}
